// ============================================================================
// Équations trigonométriques en une inconnue RÉELLE (moteur v24, refonte lot 5.3 —
// docs/moteur/REFONTE_MOTEUR.md). Fonctions PURES.
// ============================================================================
// Une mesure s'écrit en multiples rationnels de π : la valeur q représente qπ.
// L'ensemble des solutions d'une équation sur ℝ est une réunion de FAMILLES
// x = base + pas × k (k entier ; pas = 0 : une seule valeur). On le calcule
// exactement pour :
//   - un polynôme en UN cos, sin ou tan d'un argument affine en x (2sin(x) − 1 = 0,
//     cos(2x) = √3 ÷ 2, 2cos²(x) − cos(x) − 1 = 0), dont les racines sont des valeurs
//     remarquables (multiples de π ÷ 6, de π ÷ 4) ou s'écrivent f(w) (cos(3π ÷ 5)) ;
//   - a sin(u) + b cos(u) = 0 : on divise par cos(u), tan(u) = −b ÷ a ;
//   - f(u₁) = f(u₂), u₁ et u₂ affines en x (cos(x) = cos(π ÷ 2 − x), cos(x) = sin(x)) ;
//   - une relation affine en x et en des entiers k (« x = π ÷ 3 + 2kπ »).
// Sur un INTERVALLE borné, on compare les solutions qui s'y trouvent (une inéquation
// s'y étudie aussi : points où une relation s'annule, signe entre deux).
// Sinon : null (on ne sait pas).
// ============================================================================

import * as Q from './exact';
import type { Rat } from './exact';
import { evalFloat, normalize, NormalFail, polyIsZero, polyRat, type Poly } from './normal';
import {
  add,
  canon,
  div,
  fn,
  mul,
  num,
  relationsIn,
  sub,
  substitute,
  sym,
  symbolsOf,
  termKey,
  termText,
  type Relation,
  type Sorts,
  type Statement,
  type Term,
} from './term';
import { univariateSolutions } from './witness';

/** Une famille x = base + pas × k, k entier (pas = 0 : la seule valeur base). */
export interface Family {
  base: Rat;
  step: Rat;
}
/** Intervalle de l'énoncé, bornes en multiples de π (null : pas de borne). */
export interface TrigDomain {
  lo: Rat | null;
  hi: Rat | null;
  loStrict: boolean;
  hiStrict: boolean;
}

const TRIG = new Set(['cos', 'sin', 'tan']);
const UNIT: Poly = { t: new Map([['', { m: [], c: Q.ONE }]]) };
const PI = sym('π');
const SLOT = '§t';
const SLOT_S = '§s';
const SLOT_C = '§c';

/** qπ écrit comme on l'écrit : « π ÷ 3 », « −2π ÷ 3 », « 0 » */
export function piTerm(q: Rat): Term {
  if (Q.isZero(q)) return num(0);
  // « −π ÷ 3 », « −2π ÷ 3 » : le signe porte sur le numérateur
  const top = q.n === 1n ? PI : mul(num(Q.rat(q.n)), PI);
  return q.d === 1n ? top : div(top, num(Q.rat(q.d)));
}
export const piText = (q: Rat): string => termText(canon(piTerm(q)));

/** c tel que t = c·π exactement (c rationnel ; 0 compris), sinon null. */
export function piCoef(t: Term, sorts: Sorts = {}): Rat | null {
  const n = normalize(t, { sorts });
  if (!n.ok) return null;
  const d = polyRat(n.rf.d);
  if (d === null || Q.isZero(d)) return null;
  const terms = [...n.rf.n.t.values()];
  if (!terms.length) return Q.ZERO;
  if (terms.length !== 1) return null;
  const m = terms[0]!;
  if (m.m.length !== 1 || m.m[0]!.a.key !== '$π' || m.m[0]!.e !== 1) return null;
  return Q.div(m.c, d);
}
/** valeur rationnelle exacte d'un terme constant, sinon null */
function ratOf(t: Term, sorts: Sorts): Rat | null {
  const n = normalize(t, { sorts });
  if (!n.ok) return null;
  const d = polyRat(n.rf.d);
  const v = polyRat(n.rf.n);
  return d === null || v === null || Q.isZero(d) ? null : Q.div(v, d);
}
const isZeroTerm = (t: Term, sorts: Sorts): boolean => {
  const n = normalize(t, { sorts });
  return n.ok && polyIsZero(n.rf.n);
};

/** u = a·x + bπ (a rationnel non nul, b rationnel) ; null sinon. */
export function affineIn(u: Term, x: string, sorts: Sorts = {}): { a: Rat; b: Rat } | null {
  if (!symbolsOf(u).has(x) || [...symbolsOf(u)].some((s) => s !== x)) return null;
  const at = (v: number): Term => substitute(u, { [x]: num(v) });
  const a = ratOf(sub(at(1), at(0)), sorts);
  const b = piCoef(at(0), sorts);
  if (!a || !b || Q.isZero(a)) return null;
  // vérification : u − (a x + bπ) = 0
  if (!isZeroTerm(sub(u, add(mul(num(a), sym(x)), mul(num(b), PI))), sorts)) return null;
  return { a, b };
}

/** cos, sin, tan dont l'argument contient x (dans l'ordre d'apparition). */
export function trigNodes(t: Term, x: string, out: Term[] = []): Term[] {
  switch (t.k) {
    case 'add':
    case 'mul':
      t.c.forEach((c) => trigNodes(c, x, out));
      break;
    case 'div':
    case 'pow':
    case 'binom':
      trigNodes(t.a, x, out);
      trigNodes(t.b, x, out);
      break;
    case 'fn':
      if (TRIG.has(t.f) && symbolsOf(t.a).has(x)) out.push(t);
      else trigNodes(t.a, x, out);
      break;
    default:
      break;
  }
  return out;
}
/** t où le sous-terme de clé `key` est remplacé par `by` */
export function replaceKey(t: Term, key: string, by: Term): Term {
  if (termKey(t) === key) return by;
  switch (t.k) {
    case 'add':
    case 'mul':
      return { ...t, c: t.c.map((c) => replaceKey(c, key, by)) };
    case 'div':
    case 'pow':
    case 'binom':
      return { ...t, a: replaceKey(t.a, key, by), b: replaceKey(t.b, key, by) };
    case 'fn':
      return { ...t, a: replaceKey(t.a, key, by) };
    default:
      return t;
  }
}

/** q ramené dans [0 ; m[ */
function mod(q: Rat, m: Rat): Rat {
  const k = Q.div(q, m)!;
  const fl = k.n >= 0n ? k.n / k.d : -((-k.n + k.d - 1n) / k.d);
  return Q.sub(q, Q.mul(m, Q.rat(fl)));
}
const TWO = Q.rat(2);
function dedupe(xs: Rat[]): Rat[] {
  const out: Rat[] = [];
  xs.forEach((q) => {
    if (!out.some((r) => Q.eq(r, q))) out.push(q);
  });
  return out;
}

/**
 * Les angles θ de [0 ; 2π[ (multiples de π) tels que f(θ) = c ; [] s'il n'y en a
 * pas ; null si on ne sait pas les écrire exactement (cos(x) = 0,8).
 */
export function baseAngles(f: string, c: Term, sorts: Sorts = {}): Rat[] | null {
  const v = evalFloat(c, {});
  if (!Number.isFinite(v)) return null;
  if (f !== 'tan') {
    if (Math.abs(v) > 1 + 1e-9) return [];
    if (Math.abs(v) > 1 - 1e-9 && !isZeroTerm(sub(c, num(Math.sign(v))), sorts)) return null;
  }
  // valeurs remarquables : multiples de π ÷ 12 que le noyau sait calculer
  const found: Rat[] = [];
  for (let j = 0; j < 24; j++) {
    if (j % 2 !== 0 && j % 3 !== 0) continue;
    const th = Q.rat(j, 12);
    const val = Math.cos(Math.PI * Q.ratToNumber(th));
    if (f === 'tan' && Math.abs(val) < 1e-12) continue;
    const fv =
      f === 'cos'
        ? val
        : f === 'sin'
          ? Math.sin(Math.PI * Q.ratToNumber(th))
          : Math.tan(Math.PI * Q.ratToNumber(th));
    if (Math.abs(fv - v) > 1e-6) continue;
    try {
      if (isZeroTerm(sub(fn(f as 'cos', mul(num(th), PI)), c), sorts)) found.push(th);
    } catch (e) {
      if (!(e instanceof NormalFail)) throw e;
    }
  }
  if (found.length) return found;
  // c = ± g(wπ) : cos(3π ÷ 5), −sin(π ÷ 5)…
  const r = refOf(c, sorts);
  if (!r) return null;
  let { g, w } = r;
  // f et g différentes : on change de fonction par un angle associé
  if (g !== f) {
    if (f === 'tan' || g === 'tan') return null;
    // sin(w) = cos(π ÷ 2 − w) ; cos(w) = sin(π ÷ 2 − w)
    w = Q.sub(Q.rat(1, 2), w);
    g = f;
  }
  const out =
    f === 'cos' ? [w, Q.neg(w)] : f === 'sin' ? [w, Q.sub(Q.ONE, w)] : [w, Q.add(w, Q.ONE)];
  const res = dedupe(out.map((q) => mod(q, TWO)));
  // contrôle numérique (garde-fou)
  const ok = res.every((q) => {
    const a = Math.PI * Q.ratToNumber(q);
    const fv = f === 'cos' ? Math.cos(a) : f === 'sin' ? Math.sin(a) : Math.tan(a);
    return Math.abs(fv - v) < 1e-9;
  });
  return ok ? res : null;
}
/** c = ± g(wπ), g ∈ {cos, sin, tan}, ramené à + (−cos w = cos(π − w)…) */
function refOf(c: Term, sorts: Sorts): { g: string; w: Rat } | null {
  let t = canon(c);
  let sign = 1;
  if (t.k === 'mul' && t.c.length === 2 && t.c[0]!.k === 'num' && Q.eq(Q.abs(t.c[0]!.v), Q.ONE)) {
    sign = Q.sign(t.c[0]!.v);
    t = t.c[1]!;
  }
  if (t.k !== 'fn' || !TRIG.has(t.f) || symbolsOf(t.a).size) return null;
  const w0 = piCoef(t.a, sorts);
  if (!w0) return null;
  if (sign > 0) return { g: t.f, w: w0 };
  // −cos(w) = cos(π − w) ; −sin(w) = sin(−w) ; −tan(w) = tan(−w)
  return { g: t.f, w: t.f === 'cos' ? Q.sub(Q.ONE, w0) : Q.neg(w0) };
}

/* ------------------------------ résoudre une relation ------------------------------ */

/** u ≡ θ (mod 2π) avec u = a x + bπ : x = (θ − b) ÷ a + (2 ÷ |a|) k */
function familiesOfAngle(thetas: Rat[], a: Rat, b: Rat): Family[] {
  const step = Q.div(TWO, Q.abs(a))!;
  return thetas.map((th) => ({ base: Q.div(Q.sub(th, b), a)!, step }));
}

/** a sin(u) + b cos(u) (exactement) : { A, B } constants ; null sinon. */
export function homogeneous(
  e: Term,
  sNode: Term,
  cNode: Term,
  sorts: Sorts = {},
): { A: Term; B: Term } | null {
  const e1 = replaceKey(replaceKey(e, termKey(sNode), sym(SLOT_S)), termKey(cNode), sym(SLOT_C));
  if ([...symbolsOf(e1)].some((s) => s !== SLOT_S && s !== SLOT_C)) return null;
  const A = substitute(e1, { [SLOT_S]: num(1), [SLOT_C]: num(0) });
  const B = substitute(e1, { [SLOT_S]: num(0), [SLOT_C]: num(1) });
  const rebuilt = add(mul(A, sym(SLOT_S)), mul(B, sym(SLOT_C)));
  return isZeroTerm(sub(e1, rebuilt), sorts) ? { A, B } : null;
}

/**
 * Familles de solutions de « l = r » (une égalité) en x, les autres lettres étant des
 * entiers (`params`). null : on ne sait pas.
 */
export function solveEquality(
  r: Relation,
  x: string,
  params: ReadonlySet<string>,
  sorts: Sorts = {},
): Family[] | null {
  const e = sub(r.l, r.r);
  const syms = symbolsOf(e);
  if (!syms.has(x)) return null;
  if ([...syms].some((s) => s !== x && !params.has(s))) return null;
  const nodes = trigNodes(e, x);
  if (!nodes.length) return linearFamilies(e, x, params, sorts);
  if ([...syms].some((s) => params.has(s))) return null;
  const keys = [...new Set(nodes.map(termKey))];
  if (keys.length === 1) return singleNode(e, nodes[0]!, x, sorts);
  if (keys.length === 2) {
    const n1 = nodes.find((n) => termKey(n) === keys[0])!;
    const n2 = nodes.find((n) => termKey(n) === keys[1])!;
    if (n1.k !== 'fn' || n2.k !== 'fn') return null;
    // a sin(u) + b cos(u) = 0 : diviser par cos(u)
    if (termKey(n1.a) === termKey(n2.a)) {
      const [s, c] = n1.f === 'sin' && n2.f === 'cos' ? [n1, n2] : [n2, n1];
      if (s.f !== 'sin' || c.f !== 'cos') return null;
      const h = homogeneous(e, s, c, sorts);
      if (!h) return null;
      const u = affineIn(s.a, x, sorts);
      if (!u) return null;
      const aZero = isZeroTerm(h.A, sorts);
      const bZero = isZeroTerm(h.B, sorts);
      if (aZero && bZero) return null; // 0 = 0
      const [f, val] = aZero
        ? ['cos', num(0)]
        : bZero
          ? ['sin', num(0)]
          : ['tan', mul(num(-1), div(h.B, h.A))];
      const th = baseAngles(f, val, sorts);
      return th ? familiesOfAngle(th, u.a, u.b) : null;
    }
    return twoArguments(e, n1, n2, x, sorts);
  }
  return null;
}

/** Un seul cos / sin / tan de x : polynôme en f(u), racines remarquables. */
function singleNode(e: Term, node: Term, x: string, sorts: Sorts): Family[] | null {
  if (node.k !== 'fn') return null;
  const u = affineIn(node.a, x, sorts);
  if (!u) return null;
  const inT = replaceKey(e, termKey(node), sym(SLOT));
  if (symbolsOf(inT).has(x)) return null;
  const n = normalize(inT, { sorts });
  // un dénominateur en f(u) (1 ÷ cos(x)) : pas étudié ici
  if (!n.ok || polyRat(n.rf.d) === null) return null;
  const sol = univariateSolutions({ n: n.rf.n, d: UNIT }, SLOT);
  if (!sol || !sol.complete) return null;
  const out: Family[] = [];
  for (const c of sol.roots) {
    const th = baseAngles(node.f, c, sorts);
    if (!th) return null;
    out.push(...familiesOfAngle(th, u.a, u.b));
  }
  return out;
}

/** f(u₁) = f(u₂) (ou cos(u₁) = sin(u₂)) : u₁ ∓ u₂ dans 2πℤ (ou π + 2πℤ). */
function twoArguments(e: Term, n1: Term, n2: Term, x: string, sorts: Sorts): Family[] | null {
  if (n1.k !== 'fn' || n2.k !== 'fn') return null;
  // e = κ (n1 − n2), κ constant non nul
  const e1 = replaceKey(replaceKey(e, termKey(n1), sym(SLOT_S)), termKey(n2), sym(SLOT_C));
  if ([...symbolsOf(e1)].some((s) => s !== SLOT_S && s !== SLOT_C)) return null;
  const k1 = substitute(e1, { [SLOT_S]: num(1), [SLOT_C]: num(0) });
  const k2 = substitute(e1, { [SLOT_S]: num(0), [SLOT_C]: num(1) });
  const c0 = substitute(e1, { [SLOT_S]: num(0), [SLOT_C]: num(0) });
  if (!isZeroTerm(c0, sorts) || !isZeroTerm(add(k1, k2), sorts) || isZeroTerm(k1, sorts))
    return null;
  if (!isZeroTerm(sub(e1, mul(k1, sub(sym(SLOT_S), sym(SLOT_C)))), sorts)) return null;
  let a1 = affineIn(n1.a, x, sorts);
  let a2 = affineIn(n2.a, x, sorts);
  if (!a1 || !a2) return null;
  let f1 = n1.f;
  let f2 = n2.f;
  // cos(u₁) = sin(u₂) : sin(u₂) = cos(π ÷ 2 − u₂)
  if (f1 !== f2) {
    if (f1 === 'tan' || f2 === 'tan') return null;
    if (f2 === 'sin') {
      a2 = { a: Q.neg(a2.a), b: Q.sub(Q.rat(1, 2), a2.b) };
      f2 = 'cos';
    } else {
      a1 = { a: Q.neg(a1.a), b: Q.sub(Q.rat(1, 2), a1.b) };
      f1 = 'cos';
    }
  }
  // u₁ − u₂ ≡ 0 et, pour cos, u₁ + u₂ ≡ 0 ; pour sin, u₁ + u₂ ≡ π (mod 2π ; tan : mod π)
  const period = f1 === 'tan' ? Q.ONE : TWO;
  const cases: { a: Rat; b: Rat }[] = [{ a: Q.sub(a1.a, a2.a), b: Q.sub(a1.b, a2.b) }];
  if (f1 === 'cos') cases.push({ a: Q.add(a1.a, a2.a), b: Q.add(a1.b, a2.b) });
  if (f1 === 'sin') cases.push({ a: Q.add(a1.a, a2.a), b: Q.sub(Q.add(a1.b, a2.b), Q.ONE) });
  const out: Family[] = [];
  for (const cs of cases) {
    if (Q.isZero(cs.a)) {
      // aucune solution, ou toutes (identité) : on ne tranche pas une identité ici
      if (Q.isZero(mod(cs.b, period))) return null;
      continue;
    }
    // a x + bπ = period·kπ → x = −b ÷ a + (period ÷ |a|) k
    out.push({ base: Q.div(Q.neg(cs.b), cs.a)!, step: Q.div(period, Q.abs(cs.a))! });
  }
  return out;
}

/** a x + Σ gᵢ kᵢ π + dπ = 0 : x = −d ÷ a + pas × k (pas = pgcd des |gᵢ ÷ a|). */
function linearFamilies(
  e: Term,
  x: string,
  params: ReadonlySet<string>,
  sorts: Sorts,
): Family[] | null {
  const ps = [...symbolsOf(e)].filter((s) => params.has(s));
  const zero: Record<string, Term> = { [x]: num(0) };
  ps.forEach((p) => (zero[p] = num(0)));
  const e0 = substitute(e, zero);
  const a = ratOf(sub(substitute(e, { ...zero, [x]: num(1) }), e0), sorts);
  const d = piCoef(e0, sorts);
  if (!a || !d || Q.isZero(a)) return null;
  const gs: Rat[] = [];
  for (const p of ps) {
    const g = piCoef(sub(substitute(e, { ...zero, [p]: num(1) }), e0), sorts);
    if (!g) return null;
    gs.push(g);
  }
  // vérification : e = a x + Σ gᵢ kᵢ π + dπ
  const rebuilt = add(
    mul(num(a), sym(x)),
    ...ps.map((p, i) => mul(num(gs[i]!), sym(p), PI)),
    mul(num(d), PI),
  );
  if (!isZeroTerm(sub(e, rebuilt), sorts)) return null;
  let step = Q.ZERO;
  for (const g of gs) if (!Q.isZero(g)) step = ratGcd(step, Q.abs(Q.div(g, a)!));
  return [{ base: Q.div(Q.neg(d), a)!, step }];
}
function ratGcd(a: Rat, b: Rat): Rat {
  if (Q.isZero(a)) return b;
  if (Q.isZero(b)) return a;
  const g = (p: bigint, q: bigint): bigint => (q === 0n ? p : g(q, p % q));
  // pgcd(p/q, r/s) = pgcd(ps, rq) ÷ qs
  return Q.rat(g(a.n * b.d, b.n * a.d), a.d * b.d);
}

/* ------------------------------ ensembles de solutions ------------------------------ */

/** Familles d'un énoncé d'égalités (« = », « ou ») ; null sinon. */
export function statementFamilies(
  s: Statement,
  x: string,
  params: ReadonlySet<string>,
  sorts: Sorts = {},
): Family[] | null {
  if (s.k === 'expr') return null;
  const rels =
    s.k === 'set'
      ? s.clauses.every((c) => c.length === 1)
        ? s.clauses.flat()
        : null
      : relationsIn(s);
  if (!rels) return null;
  const out: Family[] = [];
  for (const r of rels) {
    if (r.rel !== '=') return null;
    const f = solveEquality(r, x, params, sorts);
    if (!f) return null;
    out.push(...f);
  }
  return out;
}

const MAX_RESIDUES = 96;
function lcmRat(a: Rat, b: Rat): Rat {
  // ppcm(p/q, r/s) = ppcm(p, r) ÷ pgcd(q, s), fractions réduites
  const g = (p: bigint, q: bigint): bigint => (q === 0n ? (p < 0n ? -p : p) : g(q, p % q));
  const l = (p: bigint, q: bigint): bigint => (p / g(p, q)) * q;
  return Q.rat(l(a.n, b.n), g(a.d, b.d));
}
/** Réduction : résidus des familles modulo P, et valeurs isolées hors de ces résidus. */
function residues(fams: Family[], P: Rat | null): { res: Rat[]; points: Rat[] } | null {
  const res: Rat[] = [];
  const points: Rat[] = [];
  for (const f of fams) {
    if (Q.isZero(f.step)) {
      points.push(f.base);
      continue;
    }
    const n = Q.div(P!, f.step)!;
    if (!Q.isInteger(n) || n.n > BigInt(MAX_RESIDUES)) return null;
    for (let i = 0n; i < n.n; i++) res.push(mod(Q.add(f.base, Q.mul(f.step, Q.rat(i))), P!));
  }
  const r = dedupe(res);
  return {
    res: r,
    points: dedupe(points).filter((p) => !(P && r.some((q) => Q.eq(q, mod(p, P))))),
  };
}
const inFamilies = (v: Rat, fams: Family[]): boolean =>
  fams.some((f) =>
    Q.isZero(f.step) ? Q.eq(f.base, v) : Q.isInteger(Q.div(Q.sub(v, f.base), f.step)!),
  );

/** Comparaison de deux réunions de familles sur ℝ. */
export function compareFamilies(
  a: Family[],
  b: Family[],
): { kind: 'equal' } | { kind: 'lost' | 'extra'; at: Rat } | null {
  let P: Rat | null = null;
  for (const f of [...a, ...b]) if (!Q.isZero(f.step)) P = P ? lcmRat(P, f.step) : f.step;
  const ra = residues(a, P);
  const rb = residues(b, P);
  if (!ra || !rb) return null;
  const witness = (r: Rat, other: Family[]): Rat | null => {
    for (let m = 0; m < 6; m++) {
      const v = P ? Q.add(r, Q.mul(P, Q.rat(m))) : r;
      if (!inFamilies(v, other)) return v;
    }
    return null;
  };
  const missing = (x: { res: Rat[]; points: Rat[] }, other: Family[]): Rat | null => {
    for (const r of x.res) {
      if (inFamilies(r, other) && P && other.every((f) => !Q.isZero(f.step))) continue;
      const w = witness(r, other);
      if (w) return w;
    }
    for (const p of x.points) if (!inFamilies(p, other)) return p;
    return null;
  };
  const lost = missing(ra, b);
  if (lost) return { kind: 'lost', at: lost };
  const extra = missing(rb, a);
  if (extra) return { kind: 'extra', at: extra };
  return { kind: 'equal' };
}

/* ------------------------------ sur un intervalle ------------------------------ */

/** « −π < x ≤ π » (une chaîne, ou une comparaison) lu comme un intervalle ; null sinon. */
export function trigDomainOf(s: Statement | null, x: string, sorts: Sorts = {}): TrigDomain | null {
  if (!s) return null;
  const rels =
    s.k === 'rel' ? relationsIn(s) : s.k === 'set' && s.clauses.length === 1 ? s.clauses[0]! : null;
  if (!rels) return null;
  const d: TrigDomain = { lo: null, hi: null, loStrict: false, hiStrict: false };
  for (const r of rels) {
    const xl = r.l.k === 'sym' && r.l.n === x;
    const xr = r.r.k === 'sym' && r.r.n === x;
    if (xl === xr || r.rel === '=') return null;
    const q = piCoef(xl ? r.r : r.l, sorts);
    if (!q) return null;
    // x < c, x ≤ c → borne haute ; c < x → borne basse
    const upper = xl ? r.rel === '<' || r.rel === '≤' : r.rel === '>' || r.rel === '≥';
    const strict = r.rel === '<' || r.rel === '>';
    if (upper) [d.hi, d.hiStrict] = [q, strict];
    else [d.lo, d.loStrict] = [q, strict];
  }
  return d;
}
export const inTrigDomain = (v: Rat, d: TrigDomain): boolean =>
  (d.lo === null || (d.loStrict ? Q.cmp(v, d.lo) > 0 : Q.cmp(v, d.lo) >= 0)) &&
  (d.hi === null || (d.hiStrict ? Q.cmp(v, d.hi) < 0 : Q.cmp(v, d.hi) <= 0));

/** Les valeurs des familles dans l'intervalle borné (croissantes) ; null si trop. */
export function familiesIn(fams: Family[], d: TrigDomain): Rat[] | null {
  if (d.lo === null || d.hi === null) return null;
  const out: Rat[] = [];
  for (const f of fams) {
    if (Q.isZero(f.step)) {
      if (inTrigDomain(f.base, d)) out.push(f.base);
      continue;
    }
    const kLo = Q.div(Q.sub(d.lo, f.base), f.step)!;
    const start = kLo.n >= 0n ? (kLo.n + kLo.d - 1n) / kLo.d : -(-kLo.n / kLo.d);
    for (let k = start; ; k++) {
      const v = Q.add(f.base, Q.mul(f.step, Q.rat(k)));
      if (Q.cmp(v, d.hi) > 0) break;
      if (inTrigDomain(v, d)) out.push(v);
      if (out.length > 200) return null;
    }
  }
  return dedupe(out).sort((p, q) => Q.cmp(p, q));
}

/** Valeur de k qui place v dans la famille (null : pas dans la famille). */
export function familyIndex(v: Rat, f: Family): bigint | null {
  if (Q.isZero(f.step)) return Q.eq(v, f.base) ? 0n : null;
  const k = Q.div(Q.sub(v, f.base), f.step)!;
  return Q.isInteger(k) ? k.n : null;
}

/* ------------------------------ étude d'un passage ------------------------------ */

export type TrigComparison =
  | { kind: 'equal' }
  | { kind: 'lost'; at: Rat }
  | { kind: 'extra'; at: Rat }
  | { kind: 'counterexample'; at: Rat };

/**
 * Deux lignes en x (radians), les autres lettres étant des entiers : mêmes solutions
 * (sur l'intervalle s'il y en a un) ? null si on ne sait pas.
 */
export function compareTrig(
  prev: Statement,
  next: Statement,
  x: string,
  params: ReadonlySet<string>,
  domain: TrigDomain | null,
  sorts: Sorts = {},
): TrigComparison | null {
  const bounded = !!domain && domain.lo !== null && domain.hi !== null;
  if (!bounded) {
    const a = statementFamilies(prev, x, params, sorts);
    const b = statementFamilies(next, x, params, sorts);
    if (!a || !b) return null;
    if (domain) return null; // demi-droite : pas étudié
    const c = compareFamilies(a, b);
    if (!c) return null;
    return c.kind === 'equal' ? c : { kind: c.kind, at: c.at };
  }
  return boundedStudy(prev, next, x, params, domain!, sorts);
}

interface RelZeros {
  key: string;
  zeros: Rat[];
}
const relKey = (r: Relation): string => `${termKey(r.l)}${r.rel}${termKey(r.r)}`;
function boundedStudy(
  prev: Statement,
  next: Statement,
  x: string,
  params: ReadonlySet<string>,
  d: TrigDomain,
  sorts: Sorts,
): TrigComparison | null {
  if (prev.k === 'expr' || next.k === 'expr') return null;
  const all: RelZeros[] = [];
  for (const r of [...relationsIn(prev), ...relationsIn(next)]) {
    const hasParam = [...symbolsOf(r.l), ...symbolsOf(r.r)].some((s) => params.has(s));
    if (hasParam && r.rel !== '=') return null;
    const fams = solveEquality({ rel: '=', l: r.l, r: r.r }, x, params, sorts);
    // une relation sans x (0 = 0…) : on ne sait pas
    if (!fams) return null;
    const zeros = familiesIn(fams, d);
    if (!zeros) return null;
    all.push({ key: relKey(r), zeros });
  }
  const pts = dedupe([
    ...all.flatMap((z) => z.zeros),
    ...(d.loStrict ? [] : [d.lo!]),
    ...(d.hiStrict ? [] : [d.hi!]),
  ]).sort((p, q) => Q.cmp(p, q));
  // points d'essai : chaque point, et un point entre deux (et aux bords ouverts)
  const marks = dedupe([d.lo!, ...pts, d.hi!]).sort((p, q) => Q.cmp(p, q));
  const tests: Rat[] = [...pts];
  for (let i = 0; i + 1 < marks.length; i++)
    tests.push(Q.div(Q.add(marks[i]!, marks[i + 1]!), TWO)!);
  tests.sort((p, q) => Q.cmp(p, q));
  const truthOf = (s: Statement, v: Rat): boolean | null => {
    const relTruth = (r: Relation): boolean | null => {
      const z = all.find((a) => a.key === relKey(r));
      if (!z) return null;
      if (z.zeros.some((q) => Q.eq(q, v))) return r.rel === '=' || r.rel === '≤' || r.rel === '≥';
      if (r.rel === '=') return false;
      const val = evalFloat(substitute(sub(r.l, r.r), { [x]: piTerm(v) }), {});
      if (!Number.isFinite(val) || Math.abs(val) < 1e-9) return null;
      return r.rel === '<' || r.rel === '≤' ? val < 0 : val > 0;
    };
    if (s.k === 'rel') return relTruth({ rel: s.rel, l: s.l, r: s.r });
    const clauses = s.k === 'or' ? s.items.map((i) => [i]) : s.k === 'set' ? s.clauses : [];
    let undecided = false;
    for (const c of clauses) {
      let ok: boolean | null = true;
      for (const r of c) {
        const t = relTruth(r);
        if (t === false) {
          ok = false;
          break;
        }
        if (t === null) ok = null;
      }
      if (ok === true) return true;
      if (ok === null) undecided = true;
    }
    return undecided ? null : false;
  };
  let converse: Rat | null = null;
  const eqPrev = prev.k === 'rel' ? prev.rel === '=' : prev.k === 'or';
  for (const v of tests) {
    if (!inTrigDomain(v, d)) continue;
    const p = truthOf(prev, v);
    const n = truthOf(next, v);
    if (p === null || n === null) return null;
    if (p && !n) return { kind: eqPrev ? 'lost' : 'counterexample', at: v };
    if (n && !p) converse ??= v;
  }
  return converse ? { kind: 'extra', at: converse } : { kind: 'equal' };
}

/** La valeur d'une solution, écrite pour l'élève : « x = 5π ÷ 6 » */
export function atText(x: string, v: Rat): string {
  return `${x} = ${piText(v)}`;
}
